import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, CheckCircle2, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { api, dataIt, formatError } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { PageHeader } from "@/components/PageHeader";

const PERIODI = [["3", "Più di 3 mesi"], ["6", "Più di 6 mesi"], ["12", "Più di 12 mesi"]];
const HEAD = ["Cliente", "Bici", "Ultimo passaggio", "Mesi", "Ultimo promemoria", ""];
const mesiDa = (d) => Math.floor((Date.now() - new Date(d)) / (30 * 864e5));

const messaggio = (t, negozio) =>
  `Ciao ${t.cliente_nome.split(" ")[0]}, ${t.ultimo_passaggio ? `l'ultimo controllo della tua ${t.bici_desc} risale al ${dataIt(t.ultimo_passaggio)}` : `non abbiamo ancora visto in officina la tua ${t.bici_desc}`}. È il momento giusto per un tagliando: regolazioni, freni, trasmissione e sicurezza. Rispondi a questo messaggio per fissare un appuntamento! ${negozio || ""}`.trim();

export default function Tagliandi() {
  const navigate = useNavigate();
  const [mesi, setMesi] = useState("6");
  const [items, setItems] = useState(null);
  const [negozio, setNegozio] = useState("");

  const load = useCallback(() => api.get("/tagliandi", { params: { mesi } }).then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e))), [mesi]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/impostazioni").then((r) => setNegozio(r.data.nome_negozio || "")); }, []);

  const contatta = async (t) => {
    if (!t.cliente_telefono) return toast.error("Il cliente non ha un numero di telefono");
    openWhatsApp(t.cliente_telefono, messaggio(t, negozio));
    await api.post(`/tagliandi/${t.bici_id}/contattato`).catch(() => {});
    load();
  };

  return (
    <div>
      <PageHeader overline="Clienti" title="Promemoria tagliando">
        <Select value={mesi} onValueChange={setMesi}>
          <SelectTrigger className="w-48 bg-white" data-testid="tagliandi-periodo-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>
      <p className="mb-4 text-sm text-slate-500" data-testid="tagliandi-count">{items ? `${items.length} bici senza passaggi in officina da più di ${mesi} mesi.` : ""}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <Table data-testid="tagliandi-table">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {HEAD.map((h) => <TableHead key={h} className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items?.map((t) => (
              <TableRow key={t.bici_id} data-testid={`tagliandi-row-${t.bici_id}`}>
                <TableCell><button className="font-semibold text-slate-900 hover:text-orange-600" onClick={() => navigate(`/clienti/${t.cliente_id}`)} data-testid={`tagliandi-cliente-${t.bici_id}`}>{t.cliente_nome}</button><p className="text-xs text-slate-500">{t.cliente_telefono || "nessun telefono"}</p></TableCell>
                <TableCell>{t.bici_desc}</TableCell>
                <TableCell>{t.ultimo_passaggio ? dataIt(t.ultimo_passaggio) : <span className="text-xs text-slate-400">Mai in officina (registrata il {dataIt(t.riferimento)})</span>}</TableCell>
                <TableCell className="font-mono font-bold">{mesiDa(t.riferimento)}</TableCell>
                <TableCell>{t.ultimo_promemoria ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {dataIt(t.ultimo_promemoria)}</span> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell className="space-x-2 whitespace-nowrap text-right">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/agenda?cliente=${t.cliente_id}&bici=${t.bici_id}`)} data-testid={`tagliandi-agenda-${t.bici_id}`}>
                    <CalendarPlus className="mr-1 h-4 w-4" /> Fissa appuntamento
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => contatta(t)} data-testid={`tagliandi-whatsapp-${t.bici_id}`} className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                    <MessageCircle className="mr-1 h-4 w-4" /> Proponi tagliando
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items && !items.length && <TableRow><TableCell colSpan={6} className="py-16 text-center text-sm text-slate-500" data-testid="tagliandi-empty">Nessuna bici da richiamare per questo periodo.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
